import { useState } from 'react';
import type { Capture, DashboardSnapshot, DayState } from '@daily/shared';
import { TopBar } from './TopBar';
import { CapturesGrid } from './CapturesGrid';
import { AmbienceRail } from './AmbienceRail';
import { HeadlineCard } from '@/components/cards/HeadlineCard';
import { CaptureButton } from '@/components/capture/CaptureButton';
import { Lightbox } from '@/components/shared/Lightbox';
import { NowPlayingExpanded } from '@/components/cards/NowPlayingExpanded';
import { WeatherDetail } from '@/components/cards/WeatherDetail';
import { TodayDetail } from '@/components/cards/TodayDetail';
import { NestDetail } from '@/components/cards/NestDetail';
import { dayStateTheme } from '@/lib/dayStateTheme';

interface BentoGridProps {
  dashboard: DashboardSnapshot;
  dayState: DayState;
}

export function BentoGrid({ dashboard, dayState }: BentoGridProps) {
  const [lightbox, setLightbox] = useState<{ src: string; caption?: string } | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [showToday, setShowToday] = useState(false);
  const [showHeating, setShowHeating] = useState(false);
  const theme = dayStateTheme[dayState];

  const openCapture = (capture: Capture) => {
    const src = capture.originalUrl ?? capture.thumbnailUrl ?? null;
    if (!src) return;
    setLightbox({ src, caption: capture.caption ?? undefined });
  };

  return (
    <>
      <div
        className="rounded-[22px] p-5 md:p-7 relative overflow-hidden transition-[background] duration-700 flex flex-col gap-4"
        style={{ background: theme.bentoGradient }}
      >
        <TopBar dashboard={dashboard} dayState={dayState} />
        <HeadlineCard headline={dashboard.headline} size="sm" />

        <div className="grid gap-4 md:[grid-template-columns:1fr_220px] lg:[grid-template-columns:1fr_260px]">
          <CapturesGrid captures={dashboard.latestCaptures} onSelect={openCapture} />
          <AmbienceRail
            dashboard={dashboard}
            onNowPlayingClick={() => setShowPlayer(true)}
            onWeatherClick={() => setShowWeather(true)}
            onTodayClick={() => setShowToday(true)}
            onHeatingClick={() => setShowHeating(true)}
            onMemoryClick={
              dashboard.memory
                ? () =>
                    setLightbox({
                      src: dashboard.memory!.photoUrl,
                      caption: dashboard.memory!.caption,
                    })
                : undefined
            }
          />
        </div>

        <CaptureButton variant="floating" />
      </div>

      {lightbox && (
        <Lightbox src={lightbox.src} caption={lightbox.caption} onClose={() => setLightbox(null)} />
      )}
      {showPlayer && (
        <NowPlayingExpanded
          initial={dashboard.nowPlaying}
          onClose={() => setShowPlayer(false)}
        />
      )}
      {showWeather && (
        <WeatherDetail weather={dashboard.weather} onClose={() => setShowWeather(false)} />
      )}
      {showToday && <TodayDetail onClose={() => setShowToday(false)} />}
      {showHeating && <NestDetail onClose={() => setShowHeating(false)} />}
    </>
  );
}
